import { markupDisclosureWords } from '@aldilivery/core';
import { Link } from 'react-router-dom';

import { CompanyDetails, DetailsToFollowNotice, companyFacts } from '../components/CompanyDetails';
import { storeConfig } from '../config';
import { money, planAllowanceWords, planDeliveryWords } from '../lib/money';

/**
 * The terms for Shoppers, in plain words.
 *
 * Written from the rules the service actually enforces: the flat fee, the most one delivery
 * carries, paying what the till says, nothing charged without a yes, nothing swapped without a
 * yes, the refund timeline and the call price. Every figure comes from configuration, so a price
 * changed there changes here. Reviewed against the Consumer Contracts Regulations 2013, the
 * Consumer Rights Act 2015 and the E-Commerce Regulations 2002 on 9 October 2026 (ruling 54,
 * docs/LEGAL_REVIEW.md). Corrected on 30 October 2026 after an audit: no connection with any
 * other business called Ozi, the area-by-area clause, and the till limit and reminder read from
 * config (Anthony, 30 October 2026). Restaurant pricing added the same day: the flat restaurant
 * fee, the smallest restaurant order, and that restaurants giving a better price are listed
 * first. Runners have their own agreement, at /runner/agreement.
 */
export function Terms(): JSX.Element {
  const name = storeConfig.productName;
  const assistant = storeConfig.assistantName;
  const { itemCharge, maximumOrderGoodsPence, maximumProductPence, runnerPaymentPence } =
    storeConfig.fees;
  const { instantRefundUpToPence, decideWithinWorkingDays } = storeConfig.problems;
  const { extras, calls, voice, restaurants } = storeConfig;
  const company = companyFacts();

  return (
    <article className="space-y-8 max-w-2xl">
      <h1 className="text-display font-bold m-0">Our terms</h1>
      <p className="m-0 text-lead">
        These are the terms of our agreement with you when you shop with us. Nothing here takes away
        your rights under UK consumer law. If you would like them read to you, or in another format,
        ring us.
      </p>
      <DetailsToFollowNotice />

      <section aria-labelledby="who" className="space-y-3">
        <h2 id="who" className="text-lead font-bold">
          Who we are
        </h2>
        <p className="m-0">
          {name} is run by {company.name}. In these terms, &ldquo;we&rdquo; and &ldquo;us&rdquo;
          means that company, and &ldquo;you&rdquo; means the Shopper.
        </p>
        <p className="m-0">We are not connected with any other business called {assistant}.</p>
        <CompanyDetails />
      </section>

      <section aria-labelledby="what" className="space-y-3">
        <h2 id="what" className="text-lead font-bold">
          What we do
        </h2>
        <p className="m-0">
          You tell us what shopping you need, by talking to {assistant}, by tapping, or by
          telephone. A Runner buys it for you at the shop and brings it to your door. Runners are
          self-employed people who work with us; we are responsible to you for the service.
        </p>
        <p className="m-0">
          {/* Every area is open (Anthony, 30 October 2026). */}
          We run across the UK, area by area, and every area is open. If there is no Runner in your
          area yet, we tell you as you place your order, before you pay, and you choose whether to
          go ahead.
        </p>
        <p className="m-0">
          Our service is for adults. You must be 18 or over to open an account, and the shopping is
          for you or your household, not for a business.
        </p>
      </section>

      <section aria-labelledby="price" className="space-y-3">
        <h2 id="price" className="text-lead font-bold">
          What it costs
        </h2>
        <ul className="m-0 ps-6 space-y-2">
          <li>
            Every product has an item charge: {money(itemCharge.basePence)} for each one under{' '}
            {money(itemCharge.everyPence)}, and {money(itemCharge.stepPence)} more for every{' '}
            {money(itemCharge.everyPence)} of its shop price. It is always included in the price we
            show or say, it is the same on every plan, and your receipt shows the shop total, the
            item charges and delivery as separate lines. No single product can cost more than{' '}
            {money(maximumProductPence)}.
          </li>
          <li>
            Delivery depends only on your plan and the value of your shopping at the shop&rsquo;s
            prices. If you pay as you go, it is {planDeliveryWords('payg')}. On {assistant}{' '}
            Membership it is {planDeliveryWords('membership')}; on {assistant} Plus or Family and
            Carer, {planDeliveryWords('plus')}. People an organisation looks after pay the
            Membership delivery. There is no minimum spend on shopping and no extra charge at busy
            times. Each monthly plan includes a number of your own orders each calendar month (UK
            time):
            {assistant} Membership, and people an organisation looks after,{' '}
            {planAllowanceWords('membership')}; {assistant} Plus {planAllowanceWords('plus')};{' '}
            {assistant} Family and Carer {planAllowanceWords('family')}. Cancelled and refunded
            orders do not count. The extra delivery charge is shown and said before you say yes, and
            is its own line on your receipt. Pay as you go has no allowance.{' '}
            {money(runnerPaymentPence)} of every delivery goes to your Runner, or{' '}
            {money(storeConfig.fees.largeOrderRunnerPaymentPence)} for an order of{' '}
            {money(storeConfig.fees.largeOrderFromPence)} or more of shopping.
          </li>
          <li>
            Restaurant, café and takeaway orders are priced on their own. Dishes are at the
            restaurant&rsquo;s own shop price, with the same item charge on each dish. Instead of
            delivery there is a flat restaurant fee of {money(restaurants.feePence)} an order,
            whatever your plan, with no extra delivery charge; {money(runnerPaymentPence)} of it
            goes to your Runner. For {money(restaurants.largeOrderFromPence)} of dishes or more the
            restaurant fee is {money(restaurants.largeOrderFeePence)}, because your Runner is paid{' '}
            {money(storeConfig.fees.largeOrderRunnerPaymentPence)}; we keep the same{' '}
            {money(restaurants.ozisSharePence)} either way. The smallest restaurant order is{' '}
            {money(restaurants.minimumOrderPence)} of dishes at the shop&rsquo;s prices, and we tell
            you before you pay if yours is smaller. A restaurant order comes from one restaurant, on
            its own: dishes cannot go in the same order as other shopping. Some restaurants give us
            a better price than their shop price; we keep the difference. Those restaurants are
            shown first, and we say so where they are listed. Some restaurants and shops have agreed
            that we add a mark-up to their prices, which we keep:{' '}
            {markupDisclosureWords(storeConfig.assistantName, 'food')} We say so next to their
            prices, and the price we show before you pay is the price you pay.
          </li>
          <li>
            One order carries up to {money(maximumOrderGoodsPence)} of shopping, which is what one
            Runner can carry. If your basket is over that, we tell you before you pay, and you
            choose: take something out or swap it, or keep everything. If you keep everything, it
            goes as linked orders of up to {money(maximumOrderGoodsPence)} each, each with its own
            Runner, up to {money(storeConfig.fees.maximumBasketGoodsPence)} in all. The first has
            your usual delivery; each one after it costs{' '}
            {money(storeConfig.fees.extraRunnerDeliveryPence)} delivery, whatever your plan. On a
            card that extra delivery is taken only when that Runner collects the order. If a further
            Runner is not needed after all (for example, the first Runner carries a small extra),
            you are not charged for them: anything already paid goes back to your card or, if that
            is not possible (such as a bank transfer), is kept on your account as Unused Runner fee
            credit and comes off your next order.
          </li>
          <li>
            The prices shown are estimates. For the shopping, you pay what the shop&rsquo;s till
            charges, shown on the receipt, plus the item charges above. We take the estimate when
            you order. When your Runner sends the till total: if it is less, we give the difference
            straight back to your card; if it is more, we take the difference from the same card, up
            to {money(storeConfig.receipts.tillExtraFloorPence)} or a fifth of the shopping
            estimate, whichever is more. We will not take more than that without speaking to you
            first. We tell you each time.
          </li>
          <li>
            In-app calls cost {calls.pencePerMinute}p a minute for each person on the call, paid by
            the Shopper. {assistant} tells you the price and waits for your yes before each call.
          </li>
          <li>
            Plans, only if you choose to join: {assistant} Membership{' '}
            {money(extras.membershipPence)} a month; {assistant} Plus {money(extras.plusPence)} a
            month; {assistant} Family and Carer {money(extras.familyPence)} a month, for up to{' '}
            {extras.familyMaximum} people. A plan is taken from your saved card when you join and
            then on the same date each month, until you cancel. Each monthly payment has its own
            receipt.
          </li>
          <li>
            Your first month of membership is free, from when you open your account (or your first
            order by telephone). During it you pay the pay-as-you-go delivery unless you choose to
            join a plan. We remind you {extras.freeMonthReminderDaysBefore} days before it ends.
            Nothing is ever taken unless you choose to join, and if you do not, you simply carry on
            paying as you go. Nobody is refused service for not joining.
          </li>
          <li>
            Extras, if you choose them: Recipe Pass {money(extras.recipePassPence)} for{' '}
            {extras.recipePassDays} days (included in every plan); {assistant} Finds It{' '}
            {money(extras.findItPence)}, given back if nothing is found (
            {extras.freeFindItsPerMonth} free a month on Membership and in the free month,{' '}
            {extras.plusFreeFindItsPerMonth} on {assistant} Plus and Family and Carer). Neither
            renews by itself. Each price is told to you and agreed before it is taken.
          </li>
          <li>All prices include any VAT that applies.</li>
        </ul>
      </section>

      <section aria-labelledby="yes" className="space-y-3">
        <h2 id="yes" className="text-lead font-bold">
          Ordering and paying: we never take money you haven&rsquo;t agreed to
        </h2>
        <ul className="m-0 ps-6 space-y-2">
          <li>
            Before you order, the shopping with its item charges, the delivery fee, the total and
            the delivery address are put to you. Your order is made, and you agree to pay, when you
            press the button to send it, or say yes when {assistant} reads it back. We then confirm
            it on the screen or by text.
          </li>
          <li>
            You pay by a card kept safely by our payment company, Stripe, by a secure payment link
            we text you, or by bank transfer. Nobody is sent to the shop until it is paid.
          </li>
          <li>
            An order made by voice only ever goes to an address already saved on your account. Above{' '}
            {money(voice.paymentCeilingPence)}, a yes by voice is not enough: you also confirm it
            with a touch.
          </li>
          <li>
            If something is not on the shelf, your Runner asks you. Nothing is swapped without your
            say. If you do not answer, it is left out, and you are not charged for it.
          </li>
          <li>
            You can ask someone you trust, such as a family member or carer, to add your card for
            you. You are responsible for orders made on your account, so keep your PIN to yourself
            and tell us straight away if your phone is lost.
          </li>
        </ul>
      </section>

      <section aria-labelledby="not" className="space-y-3">
        <h2 id="not" className="text-lead font-bold">
          What we cannot bring
        </h2>
        <p className="m-0">
          Alcohol, tobacco, medicines, cash and anything else with an age limit are not delivered.
          Your Runner will leave out anything the shop or the law does not allow us to bring, and
          you are not charged for it.
        </p>
      </section>

      <section aria-labelledby="delivery" className="space-y-3">
        <h2 id="delivery" className="text-lead font-bold">
          Delivery
        </h2>
        <p className="m-0">
          We tell you when a Runner has your order, when it is on its way and when it is delivered.
          Your Runner follows your door instructions. If your Runner cannot reach you at the door,
          they will try to call you in the app, and a person will contact you to agree what happens
          next. If we cannot deliver at all, we refund you in full.
        </p>
      </section>

      <section aria-labelledby="cancel" className="space-y-3">
        <h2 id="cancel" className="text-lead font-bold">
          Changing your mind
        </h2>
        <ul className="m-0 ps-6 space-y-2">
          <li>
            When you send an order, you ask us to start straight away. You can cancel it free, with
            a full refund, at any time before your Runner starts shopping. Ring us or email us. You
            can use words like: &ldquo;I want to cancel my order&rdquo;, with your name and the
            order number.
          </li>
          <li>
            Once your Runner has started shopping, you can still cancel, but you pay for the
            shopping already bought and a fair part of the delivery fee, because the work has been
            done for you.
          </li>
          <li>
            Once your shopping is delivered, our service is complete, and the usual 14 days to
            change your mind no longer applies to it. Fresh, chilled and frozen food cannot be
            returned because you changed your mind. None of this affects your rights when something
            is wrong, below.
          </li>
          <li>
            Plans: you can cancel at any time, as easily as you joined: with one button in Settings
            or on the plans page, by saying &ldquo;cancel my membership&rdquo; to {assistant}, on
            the telephone too, or by ringing or emailing us. Nothing more is taken, and you keep the
            plan until the end of the month you have paid for. Within 14 days of first joining you
            may also ask for a refund, less a fair part for the days you have already had. We tell
            you before any price change.
          </li>
          <li>
            Recipe Pass: you can cancel within 14 days of buying, by ringing or emailing us. We
            refund you, less a fair part for the days you have already had. Gift cards: you can
            cancel within 14 days for a full refund if the card has not been used.
          </li>
        </ul>
      </section>

      <section aria-labelledby="wrong" className="space-y-3">
        <h2 id="wrong" className="text-lead font-bold">
          When something goes wrong, and refunds
        </h2>
        <ul className="m-0 ps-6 space-y-2">
          <li>
            Tell us on the order&rsquo;s &ldquo;Report a problem&rdquo; page in the app, or ring us.
            You can add photos, voice notes or a note.
          </li>
          <li>
            Asking for {money(instantRefundUpToPence)} or less back? It is refunded straight away.
          </li>
          <li>
            Otherwise a person decides within {decideWithinWorkingDays} working days and tells you
            why. A refund goes back to your card the day it is decided, and always within 14 days. A
            refund for a bank transfer is paid back to your bank account by a person, and can take a
            few days longer.
          </li>
          <li>
            Your rights under the Consumer Rights Act 2015 always apply: the goods must be of
            satisfactory quality, fit for purpose and as described, and our service must be done
            with reasonable care and skill. If they are not, you are entitled to have it put right,
            or to a refund.
          </li>
        </ul>
      </section>

      <section aria-labelledby="responsible" className="space-y-3">
        <h2 id="responsible" className="text-lead font-bold">
          What we are responsible for
        </h2>
        <ul className="m-0 ps-6 space-y-2">
          <li>
            We are responsible for loss or damage you suffer that is a foreseeable result of us
            breaking these terms or not taking reasonable care. Loss is foreseeable if it was
            obvious it would happen, or if we both knew it might when you ordered.
          </li>
          <li>
            We never limit our responsibility for death or injury caused by our negligence or that
            of our Runners, for fraud, or for anything else the law does not allow us to limit.
          </li>
          <li>
            We are not responsible for delays caused by events outside our control, such as severe
            weather or a shop closing. If that happens, we tell you, and you can cancel for a full
            refund.
          </li>
        </ul>
      </section>

      <section aria-labelledby="help" className="space-y-3">
        <h2 id="help" className="text-lead font-bold">
          Help and adjustments
        </h2>
        <p className="m-0">
          We built {name} for everyone, and especially for people who are blind, partially sighted,
          older, or find shopping hard. Every screen aims to meet the WCAG 2.2 AA accessibility
          standard and works with screen readers and Braille displays. You can order by voice, by
          touch or by telephone. If anything makes it hard for you to use our service, tell us, and
          we will make reasonable changes to help, as the Equality Act 2010 asks of us, such as
          reading these terms to you, ringing you instead of texting, or giving you more time.
        </p>
      </section>

      <section aria-labelledby="account" className="space-y-3">
        <h2 id="account" className="text-lead font-bold">
          Your account, and changes to these terms
        </h2>
        <ul className="m-0 ps-6 space-y-2">
          <li>
            You can close your account at any time, in Settings, by asking{' '}
            {storeConfig.assistantName} to close your account, or by ringing us. It waits{' '}
            {storeConfig.accountDeletion.recycleBinDays} days in case you change your mind.
          </li>
          <li>
            {name} may contact you, as an account holder, through the app: by a message, or by a
            call, which is recorded.
          </li>
          <li>
            We may close an account that is used for fraud or to abuse a Runner or our staff. We
            will tell you why, unless the law stops us.
          </li>
          <li>
            If we change these terms, we tell you before the change takes effect. A change never
            applies to an order you have already made.
          </li>
        </ul>
      </section>

      <section aria-labelledby="complaints" className="space-y-3">
        <h2 id="complaints" className="text-lead font-bold">
          Complaints, and the law that applies
        </h2>
        <p className="m-0">
          If you are unhappy, ring us or email us, and a person will look into it and reply within{' '}
          {decideWithinWorkingDays} working days. If we cannot agree, free advice is available from
          the Citizens Advice consumer helpline, on 0808 223 1133, and you can take a claim to
          court.
        </p>
        <p className="m-0">
          These terms are under the law of England and Wales. If you live in Scotland or Northern
          Ireland, you keep the protections of the law where you live, and you can bring a claim in
          your own courts.
        </p>
        <p className="m-0">These terms were last reviewed on 30 October 2026.</p>
      </section>

      <ul className="flex flex-wrap gap-2 list-none m-0 p-0">
        <li>
          <Link to="/privacy" className="control bg-paper/10 text-paper underline">
            Privacy
          </Link>
        </li>
        <li>
          <Link to="/cookies" className="control bg-paper/10 text-paper underline">
            What we keep on your device
          </Link>
        </li>
      </ul>
    </article>
  );
}
